import { useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';

import { setLoadingSave } from '../../store/slices/imageSlice';
import { updateImageLayout } from '../api/imageLayoutApi';
import { AppDispatch } from '../../store/store';

const handleSaveLayout = async (dispatch: AppDispatch, imageLayout: any, imageProjectId: any) => {
    if (!imageLayout || !imageProjectId) return;
    dispatch(setLoadingSave(true));
    try {
        await dispatch(
            updateImageLayout({
                imageProjectId: imageProjectId,
                layoutId: imageLayout.id,
                layout: {
                    slug: imageLayout.slug,
                    order: imageLayout.order,
                    canvas_color: imageLayout.canvas_color,
                    gap_size: imageLayout.gap_size,
                    slot_count: imageLayout.slot_count,
                    transformation: imageLayout.transformation,
                    inline_text: imageLayout.inline_text,
                },
            }),
        ).unwrap();
    } finally {
        dispatch(setLoadingSave(false));
    }
};
export const useDebouncedSaveLayout = (
    {
        dispatch,
        imageLayout,
        imageProjectId,
    }: {
        dispatch: any;
        imageLayout: any;
        imageProjectId: string;
    },
    delay = 1000,
) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const prevRef = useRef<{
        id: string | null;
        gap_size: any;
        canvas_color: any;
    }>({ id: null, gap_size: null, canvas_color: null });

    const initialized = useRef(false);

    useEffect(() => {
        if (!imageLayout) return;

        if (!initialized.current) {
            prevRef.current = {
                id: imageLayout.id,
                gap_size: imageLayout.gap_size,
                canvas_color: imageLayout.canvas_color,
            };
            initialized.current = true;
            return;
        }
        const sameId = imageLayout.id === prevRef.current.id;
        const gapChanged = !isEqual(imageLayout.gap_size, prevRef.current.gap_size);
        const colorChanged = !isEqual(imageLayout.canvas_color, prevRef.current.canvas_color);

        if (!gapChanged && !colorChanged) return;

        if (gapChanged || colorChanged) {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                handleSaveLayout(dispatch, imageLayout, imageProjectId);
            }, delay);
        }

        prevRef.current = {
            id: imageLayout.id,
            gap_size: imageLayout.gap_size,
            canvas_color: imageLayout.canvas_color,
        };

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [imageLayout?.gap_size, imageLayout?.canvas_color, delay]);
};
